import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { useTheme } from '@mui/material';

import useLocales from 'src/hooks/useLocales';

import { addNotification } from 'src/redux/slices/systemNotifications';
import { SystemNotification } from 'src/@types/system-notifications/types';

import useAuth from './useAuth';
import Iconify from '../components/Iconify';
import { IconButtonAnimate } from '../components/animate';

//-------------------------------------------------------------

const useNotifications = () => {
  const { user } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useLocales();
  const theme = useTheme();

  const createNotification = useCallback(
    (type: number, key: any, options?: any) => {
      if (!user) {
        return;
      }

      addNotification({
        localeObject: { key, options },
        type,
        dateTime: new Date(),
      } as SystemNotification);
    },
    [user]
  );

  const enqueueSuccess = useCallback(
    (key: any, options?: any) => {
      createNotification(1, key, options);
      enqueueSnackbar(t(key, options), {
        variant: 'success',
        action: (key) => (
          <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
            <Iconify icon="eva:close-fill" sx={{ color: theme.palette.success.darker }} />
          </IconButtonAnimate>
        ),
      });
    },
    [createNotification, enqueueSnackbar, t, closeSnackbar, theme.palette.success.darker]
  );

  const enqueueError = useCallback(
    (key: any, options?: any) => {
      createNotification(2, key, options);
      enqueueSnackbar(t(key, options), {
        variant: 'error',
        action: (key) => (
          <IconButtonAnimate size="small" onClick={() => closeSnackbar(key)}>
            <Iconify icon="eva:close-fill" sx={{ color: theme.palette.error.darker }} />
          </IconButtonAnimate>
        ),
      });
    },
    [createNotification, enqueueSnackbar, t, closeSnackbar, theme.palette.error.darker]
  );

  return { enqueueSuccess, enqueueError };
};

export default useNotifications;
